import type { Theme } from "@mui/material/styles";

import ChevronRight from "@mui/icons-material/ChevronRight";
import Close from "@mui/icons-material/Close";
import {
  alpha,
  Box,
  ButtonBase,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useLayoutEffect, useRef, useState } from "react";

import { SCOUT_SUGGESTION_CATEGORIES, type ScoutSuggestionCategory } from "./suggestions";

/** Preferred cap for the scrollable list when there's plenty of room below it. */
const DEFAULT_MAX_LIST_HEIGHT = 360;
/** Never shrink the list below this — keeps a couple items visible on short screens. */
const MIN_MAX_LIST_HEIGHT = 160;
/** Breathing room between the bottom of the list and the viewport edge. */
const LIST_BOTTOM_MARGIN = 24;
const ICON_ONLY_CHIP_SIZE_PX = 38;

function getSuggestionCategoryChipSx({
  selected,
  iconOnly,
  theme,
}: {
  selected: boolean;
  iconOnly: boolean;
  theme: Theme;
}) {
  return {
    height: ICON_ONLY_CHIP_SIZE_PX,
    px: iconOnly ? 0 : 0.5,
    width: iconOnly ? ICON_ONLY_CHIP_SIZE_PX : undefined,
    minWidth: iconOnly ? ICON_ONLY_CHIP_SIZE_PX : undefined,
    justifyContent: iconOnly ? "center" : undefined,
    fontWeight: 500,
    fontSize: "0.875rem",
    borderColor: selected ? "transparent" : "divider",
    color: selected ? theme.palette.primary.main : "text.primary",
    backgroundColor: selected ? alpha(theme.palette.primary.main, 0.1) : "transparent",
    "& .MuiChip-icon": {
      margin: iconOnly ? 0 : undefined,
      color: selected ? theme.palette.primary.main : "text.secondary",
    },
    ...(iconOnly
      ? {
          pl: 0,
          pr: 0,
          "& .MuiChip-label": {
            display: "none",
            width: 0,
            p: 0,
          },
        }
      : {}),
    "&:hover": {
      backgroundColor: selected
        ? alpha(theme.palette.primary.main, 0.16)
        : theme.palette.action.hover,
    },
  };
}

function SuggestionCategoryChip({
  category,
  selected,
  disabled,
  iconOnly,
  onToggle,
}: {
  category: ScoutSuggestionCategory;
  selected: boolean;
  disabled: boolean;
  iconOnly: boolean;
  onToggle: () => void;
}) {
  const theme = useTheme();
  const Icon = category.Icon;

  return (
    <Tooltip title={category.label}>
      <Box component="span" sx={{ display: "inline-flex" }}>
        <Chip
          icon={<Icon />}
          label={category.label}
          variant="outlined"
          clickable
          disabled={disabled}
          aria-expanded={selected}
          aria-label={category.label}
          onClick={onToggle}
          sx={getSuggestionCategoryChipSx({ selected, iconOnly, theme })}
        />
      </Box>
    </Tooltip>
  );
}

/**
 * Progressive-disclosure starter prompts: a row of category chips that expand to
 * reveal the actual questions. Picking a question sends it to Scout.
 *
 * The expanded panel is absolutely positioned so opening/switching categories
 * never reflows the surrounding layout (no scroll jump).
 */
export function SuggestionMenu({
  onPick,
  disabled = false,
}: {
  onPick: (prompt: string) => void;
  disabled?: boolean;
}) {
  const theme = useTheme();
  const iconOnlyChips = useMediaQuery(theme.breakpoints.down("sm"));
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const active = SCOUT_SUGGESTION_CATEGORIES.find((category) => category.key === activeKey) ?? null;

  // The panel is absolutely positioned near the vertical center of the screen, so
  // a fixed max-height can push the bottom of the list past the viewport where it
  // can't be scrolled into view. Size the scroll region to the space actually
  // available below its top edge so every suggestion stays reachable.
  const listRef = useRef<HTMLDivElement | null>(null);
  const [maxListHeight, setMaxListHeight] = useState(DEFAULT_MAX_LIST_HEIGHT);

  useLayoutEffect(() => {
    if (!activeKey) {
      return;
    }
    const updateMaxHeight = () => {
      const node = listRef.current;
      if (!node) {
        return;
      }
      const { top } = node.getBoundingClientRect();
      const available = window.innerHeight - top - LIST_BOTTOM_MARGIN;
      setMaxListHeight(Math.max(MIN_MAX_LIST_HEIGHT, Math.min(DEFAULT_MAX_LIST_HEIGHT, available)));
    };
    updateMaxHeight();
    window.addEventListener("resize", updateMaxHeight);
    return () => window.removeEventListener("resize", updateMaxHeight);
  }, [activeKey]);

  return (
    <Box sx={{ position: "relative", width: "100%", maxWidth: 720 }}>
      <Stack
        direction="row"
        spacing={1.5}
        flexWrap="nowrap"
        justifyContent="center"
        sx={{ maxWidth: "100%" }}
      >
        {SCOUT_SUGGESTION_CATEGORIES.map((category) => {
          const selected = category.key === activeKey;
          return (
            <SuggestionCategoryChip
              key={category.key}
              category={category}
              selected={selected}
              disabled={disabled}
              iconOnly={iconOnlyChips}
              onToggle={() => setActiveKey(selected ? null : category.key)}
            />
          );
        })}
      </Stack>

      {/* Absolutely positioned so expansion never pushes page layout. */}
      <Box sx={{ position: "absolute", top: "100%", left: 0, right: 0, mt: 1.5, zIndex: 5 }}>
        <Collapse in={Boolean(active)} unmountOnExit>
          {active ? (
            <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, py: 1.25 }}>
                <active.Icon fontSize="small" sx={{ color: "text.secondary" }} />
                <Typography variant="subtitle2" sx={{ flex: 1 }}>
                  {active.label}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setActiveKey(null)}
                  aria-label="Close suggestions"
                >
                  <Close fontSize="small" />
                </IconButton>
              </Stack>
              <Divider />
              <Box ref={listRef} sx={{ maxHeight: maxListHeight, overflowY: "auto" }}>
                {active.items.map((item, index) => (
                  <Box key={item.label}>
                    {index > 0 ? <Divider /> : null}
                    <ButtonBase
                      disabled={disabled}
                      onClick={() => onPick(item.prompt)}
                      sx={{
                        width: "100%",
                        justifyContent: "space-between",
                        textAlign: "left",
                        gap: 1.5,
                        px: 2,
                        py: 1.5,
                        "&:hover": { backgroundColor: "action.hover" },
                        "& .suggestion-chevron": { opacity: 0, transition: "opacity 120ms ease" },
                        "&:hover .suggestion-chevron": { opacity: 1 },
                      }}
                    >
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        {item.prompt}
                      </Typography>
                      <ChevronRight
                        className="suggestion-chevron"
                        fontSize="small"
                        sx={{ flexShrink: 0, color: "text.secondary", alignSelf: "center" }}
                      />
                    </ButtonBase>
                  </Box>
                ))}
              </Box>
            </Paper>
          ) : null}
        </Collapse>
      </Box>
    </Box>
  );
}
